import moment from "moment";

import {
  CustomerSupplierListType,
  DefaultTdsTcsDetailsType,
  ShippingAddresses,
} from "@/redux/clientSettings/clientSettingTypes";
import {
  ExpensesInvoiceItem,
  InvoicesList,
  RevenueInvoiceItem,
  PaymentStatusFilterKeyType,
  FilterStateType,
  InitialStateInvoicingAdvanceType,
  LinkedSingleColumnType,
  InvoiceType,
  TDSTCSDetailType,
  TDSTCSDetailApiReturnType,
  SingleColumnTypeWithValue,
  SingleColumnType,
} from "@/redux/invoicingAdvance/invoicingAdvance-types";
import { CurrencyExchangeDetailsType } from "@/redux/masterData/masterTypes";

import {
  getAllStockItemListApi,
  getAllTypesLedgerListApi,
  getAllTypesWithTaxListApi,
  getDocumentTypeListingApi,
  getStockMappedBatchListApi,
} from "@/apis/clientSettingApis";
import {
  getAllMasterDataCurrencyListApi,
  getMasterDataUsersDepartmentApi,
  getMasterTcsModuleApi,
  getMasterTdsModuleApi,
} from "@/apis/masterDataApi";

import {
  getAllBusinessAddressesApi,
  getAllCustomerSupplierApi,
  getAllLedgersListApi,
  getInvoicingAdvanceNotesListApi,
} from "@/components/InvoicingAdvance/apis/invoicingAdvance-api";
import {
  ALLOCATION_STATUS_CHECKBOXES,
  COLUMNS_USING_TO_OPTION_INITIAL,
  INVOICINGADVANCETABS,
  SKU_NA_OPTION,
  TRANSACTION_TYPES,
} from "@/components/InvoicingAdvance/helpers/invoicingAdvance-constant";
import {
  AutoCalculationType,
  CurrencyType,
  ExpenseSalesTabData,
  ExtendedFetchers,
  FilterByPaymentStatusType,
  FormType,
  InvoicingAdvanceActionToolbarProps,
  InvoicingAdvanceMode,
  LabeledValue,
  OthersTabDataType,
  SinglePdfHeaderType,
  valueType,
} from "@/components/InvoicingAdvance/helpers/invoicingAdvance-type";

import { STATES_LIST_WITH_CODE } from "@/constant/stateListConstants";

import { isEmailValid } from "@/utils/commonFunctions";

export const getTabs = ({
  isExpenseEntryPresent,
  isSalesEntryPresent,
  isOthersEntriesPresent,
  isManufacturingPresent,
}: {
  isExpenseEntryPresent: boolean;
  isSalesEntryPresent: boolean;
  isOthersEntriesPresent: boolean;
  isManufacturingPresent: boolean;
}) => {
  const visibilityMap: Record<string, boolean> = {
    expenses: isExpenseEntryPresent,
    revenues: isSalesEntryPresent,
    others: isOthersEntriesPresent,
    manufacturingJournal: isManufacturingPresent,
  };

  return INVOICINGADVANCETABS.map((tab) => ({
    ...tab,
    display: visibilityMap[tab.label] ?? tab.display,
  }));
};

const isExpenseInvoice = (
  invoice: ExpensesInvoiceItem | RevenueInvoiceItem
): invoice is ExpensesInvoiceItem => {
  return "supplier_name" in invoice;
};

export const transformInvoicesList = (
  invoiceList: InvoicesList
): ExpenseSalesTabData[] => {
  if (!invoiceList || !Array.isArray(invoiceList)) {
    return [];
  }

  return invoiceList.reduce<ExpenseSalesTabData[]>((acc, invoice) => {
    if ("others_id" in invoice) return acc;

    const base = {
      id: invoice.id,
      isInvoiceOCRCreated: !!invoice.is_invoice_ocr_created,
      invoiceCategory: invoice.invoice_category,
      documentType: invoice.document_type,
      eWayBillStatus: invoice.e_way_bill_status,
      voucherInvoiceNumber: invoice.voucher_invoice_number,
      createdAt: invoice.created_at,
      voucherInvoiceDate: invoice.voucher_invoice_date,
      netAmount: invoice.net_amount,
      upload: invoice.upload,
      paymentStatus: invoice.payment_status,
      companyName: invoice.company_name,
      firstName: invoice.first_name,
      lastName: invoice.last_name,
      approvalDetails: invoice.approval_details,
      updatedAt: invoice.updated_at,
      commentCount: invoice.comment_count,
      creditNoteMapping: invoice.credit_note_mapping,
      sentOverdueEmail: invoice.sent_overdue_email,
      lastEmailSentAt: invoice.last_email_sent_at,
      lastEmailTo: invoice.last_email_to,
      isLock: invoice.is_lock,
      isgstr3bfiled: invoice.is_gstr3b_filed,
      isSignedOff: invoice.is_signed_off,
      source: invoice?.source,
    };

    if (isExpenseInvoice(invoice)) {
      acc.push({
        ...base,
        entity: invoice.supplier_name,
      });
    } else {
      acc.push({
        ...base,
        entity: invoice.customer_name,
        isgstr1filed: invoice.is_gstr1_filed,
        eInvoiceStatus: invoice.einvoicing_status,
        eInvoiceAt: invoice.einvoicing_at,
        salesType: invoice.sales_type,
        customerName: invoice.customer_name,
      });
    }

    return acc;
  }, []);
};

export function getActivePaymentStatus(
  statusRecord: Record<PaymentStatusFilterKeyType, boolean>,
  statusList: FilterByPaymentStatusType[]
): string | undefined {
  const activeKeys = Object.entries(statusRecord)
    .filter(([_, value]) => value)
    .map(([key]) => key as PaymentStatusFilterKeyType);

  if (activeKeys.length === 0 || activeKeys.includes("all")) {
    return undefined;
  }

  const activeValues = statusList
    .filter((item) => activeKeys.includes(item.key) && item.key !== "all")
    .map((item) => item.value);

  return activeValues.length ? activeValues.join(",") : undefined;
}

export function getInitialLocalStateForDrawerFilter({
  drawerFilters,
  createdThroughList,
  invoiceCategoryList,
  invoiceDocumentTypeList,
}: Pick<
  InvoicingAdvanceActionToolbarProps,
  | "drawerFilters"
  | "createdThroughList"
  | "invoiceCategoryList"
  | "invoiceDocumentTypeList"
>) {
  const { createdThrough, invoiceCategory, invoiceDocumentType, allocation } =
    drawerFilters;

  const resolvedAllocation =
    allocation && allocation?.length > 0
      ? allocation
      : ALLOCATION_STATUS_CHECKBOXES;

  // If all already have values, return as is
  if (
    createdThrough?.length > 0 &&
    invoiceCategory?.length > 0 &&
    invoiceDocumentType?.length > 0
  ) {
    return {
      ...drawerFilters,
      allocation: resolvedAllocation,
      branch: drawerFilters.branch ?? null,
    };
  }

  // Otherwise, fill missing ones
  return {
    ...drawerFilters,
    branch: drawerFilters.branch ?? null,
    createdThrough:
      createdThrough?.length > 0 ? createdThrough : createdThroughList,
    invoiceCategory:
      invoiceCategory?.length > 0 ? invoiceCategory : invoiceCategoryList,
    invoiceDocumentType:
      invoiceDocumentType?.length > 0
        ? invoiceDocumentType
        : invoiceDocumentTypeList,
    allocation: resolvedAllocation,
  };
}

export function isDrawerFilterApplyDisabled(
  currentFilterLocalState: FilterStateType,
  previousFilterLocalState: FilterStateType
) {
  // Check if date filters are applied
  const hasDateFilter =
    currentFilterLocalState.date.startDate &&
    currentFilterLocalState.date.endDate;

  // Check if any radio filters are applied (non-empty strings)
  const hasApprovalFilter = currentFilterLocalState.approval !== "";
  const hasFileAttachmentFilter = currentFilterLocalState.fileAttachment !== "";
  const hasCommentFilter = currentFilterLocalState.comment !== "";

  // Check if any invoice category checkboxes are checked
  const hasInvoiceCategoryFilter = currentFilterLocalState.invoiceCategory.some(
    (category) => category.isChecked
  );

  // Check if any document type checkboxes are checked
  const hasDocumentTypeFilter =
    currentFilterLocalState.invoiceDocumentType.some(
      (docType) => docType.isChecked
    );

  // Check if any allocation status checkboxes are checked
  const hasAllocationFilter = (currentFilterLocalState?.allocation ?? []).some(
    (status) => status?.isChecked
  );

  // Check if any created through checkboxes are checked
  const hasCreatedThroughFilter = (
    currentFilterLocalState?.createdThrough ?? []
  ).some((source) => source?.isChecked);

  // Check if a branch is selected
  const hasBranchFilter = !!currentFilterLocalState?.branch?.value;

  // OCR filter edge case handling:
  // Check if OCR filter was explicitly changed from true to false
  const wasOCRFilterChangedFromTrueToFalse =
    previousFilterLocalState.ocrFilter === true &&
    currentFilterLocalState.ocrFilter === false;

  // Check if OCR filter is currently true (show only OCR items)
  const isOCRFilterTrue = currentFilterLocalState.ocrFilter === true;

  // OCR filter is considered active if:
  // 1. It's currently true, OR
  // 2. It was explicitly changed from true to false (user wants to show non-OCR items)
  const isOCRFilterActive =
    isOCRFilterTrue || wasOCRFilterChangedFromTrueToFalse;

  const isLockUnlockFilterTrue =
    currentFilterLocalState.lockUnlockFilter === true;

  const wasLockUnlockFilterChangedFromTrueToFalse =
    previousFilterLocalState.lockUnlockFilter === true &&
    currentFilterLocalState.lockUnlockFilter === false;

  // Check if any other filters are active
  const hasAnyOtherFilter =
    hasDateFilter ||
    hasApprovalFilter ||
    hasFileAttachmentFilter ||
    hasCommentFilter ||
    hasInvoiceCategoryFilter ||
    hasDocumentTypeFilter ||
    hasAllocationFilter ||
    hasCreatedThroughFilter ||
    hasBranchFilter ||
    isLockUnlockFilterTrue ||
    wasLockUnlockFilterChangedFromTrueToFalse;

  const count =
    (hasDateFilter ? 1 : 0) +
    (hasApprovalFilter ? 1 : 0) +
    (hasFileAttachmentFilter ? 1 : 0) +
    (hasCommentFilter ? 1 : 0) +
    (isOCRFilterTrue ? 1 : 0) +
    (hasInvoiceCategoryFilter ? 1 : 0) +
    (hasDocumentTypeFilter ? 1 : 0) +
    (hasAllocationFilter ? 1 : 0) +
    (hasCreatedThroughFilter ? 1 : 0) +
    (hasBranchFilter ? 1 : 0) +
    (isLockUnlockFilterTrue ? 1 : 0);
  // Return true (disabled) only if NO filters are applied
  return {
    isApplyDisabled: !hasAnyOtherFilter && !isOCRFilterActive,
    countOfFilterApplied: count,
  };
}

export function getDrawerFilterParams(drawerFilters: FilterStateType) {
  const params: Record<string, string | number | Date> = {};

  const { date: dateFilter } = drawerFilters;

  if (dateFilter.startDate && dateFilter.endDate) {
    const isCreated = dateFilter.dateType === "1";
    const startKey = isCreated
      ? "created_at_start_date"
      : "invoice_date_start_date";
    const endKey = isCreated ? "created_at_end_date" : "invoice_date_end_date";
    params[startKey] = moment(dateFilter.startDate).format("DD-MM-YYYY");
    params[endKey] = moment(dateFilter.endDate).format("DD-MM-YYYY");
  }

  if (drawerFilters.ocrFilter) {
    params.is_invoice_ocr_created = 1;
  }

  // invoiceCategory
  if (Array.isArray(drawerFilters.invoiceCategory)) {
    for (const { value, isChecked } of drawerFilters.invoiceCategory) {
      if (isChecked && value) params[value] = 1;
    }
  }

  // createdThrough
  if (Array.isArray(drawerFilters.createdThrough)) {
    const selected = drawerFilters.createdThrough
      .filter((x) => x?.isChecked && x?.value)
      .map((x) => String(x.value).trim())
      .filter(Boolean);
    if (selected.length) {
      params.source = selected.join(",");
    }
  }

  // documentType
  if (Array.isArray(drawerFilters.invoiceDocumentType)) {
    const selected = drawerFilters.invoiceDocumentType
      .filter((x) => x?.isChecked && x?.value)
      .map((x) => String(x.value).trim())
      .filter(Boolean);
    if (selected.length) {
      params.document_type = selected.join(",");
    }
  }

  if (drawerFilters.approval != "") {
    params.approved = drawerFilters.approval;
  }

  if (drawerFilters.comment != "") {
    params.comment = drawerFilters.comment;
  }

  if (drawerFilters.fileAttachment != "") {
    params.attachment = drawerFilters.fileAttachment;
  }

  if (drawerFilters.lockUnlockFilter) {
    params.lock_invoices = 1;
  }

  // allocation status
  if (Array.isArray(drawerFilters.allocation)) {
    const selected = drawerFilters.allocation
      .filter((x) => x?.isChecked && x?.value)
      .map((x) => String(x.value).trim())
      .filter(Boolean);
    if (selected.length) {
      params.allocation_status = selected.join(",");
    }
  }

  // branch
  if (drawerFilters.branch?.value) {
    params.branch_id = String(drawerFilters.branch.value);
  }

  return params;
}

export function getSortingParams(
  sorting: InitialStateInvoicingAdvanceType["filters"]["sorting"]
) {
  const params: Record<string, string | number | Date> = {};

  // Mapping of sorting options to API parameters
  const sortingConfig = {
    invoice_date_asc: { key: "date_of_invoice", value: "asc" },
    invoice_date_desc: { key: "date_of_invoice", value: "desc" },
    invoice_number_asc: { key: "invoice_voucher_number", value: "asc" },
    invoice_number_desc: { key: "invoice_voucher_number", value: "desc" },
  };

  const config = sortingConfig[sorting as keyof typeof sortingConfig];

  if (config) {
    params[config.key] = config.value;
  }

  return params;
}

export function isLabeledValue(obj: unknown): obj is LabeledValue {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "label" in obj &&
    "value" in obj &&
    typeof (obj as any).label === "string" &&
    (typeof (obj as any).value === "string" ||
      typeof (obj as any).value === "number")
  );
}
// true when the "NA" option is selected in sku_code
export function isSkuNA(skuCode: unknown): boolean {
  return isLabeledValue(skuCode) && skuCode.value === SKU_NA_OPTION.value;
}
export function isDate(value: unknown): value is Date {
  return value instanceof Date && !isNaN(value.getTime());
}

export function getPreStoredOptions(
  columnData: Record<string, valueType>,
  optionsKey: string
): LabeledValue[] | undefined {
  const optionsRaw = columnData[optionsKey];

  const isValidOptionsArray =
    optionsRaw &&
    typeof optionsRaw !== "string" &&
    typeof optionsRaw !== "boolean" &&
    !(optionsRaw instanceof Date) &&
    !(optionsRaw instanceof Array && "file_name" in (optionsRaw[0] || {}));

  if (
    isValidOptionsArray &&
    Array.isArray(optionsRaw) &&
    optionsRaw.length === 2
  ) {
    const options = optionsRaw as unknown as LabeledValue[];
    if (Array.isArray(options) && options.length > 0) {
      return options;
    }
  }

  return undefined;
}

export function getEffectiveDataType(
  column: LinkedSingleColumnType,
  columnData: Record<string, valueType>
): LinkedSingleColumnType["data_type"] {
  // Handle unit column with multiple units
  if (column.column_name === "unit") {
    const unitOptions = getPreStoredOptions(columnData, "_unitOptions");
    if (unitOptions && unitOptions.length > 0) {
      // Render as dropdown when multiple units are available
      return "json";
    }

    // Only check SKU data directly if _unitOptions was never set (for edit mode)
    // If _unitOptions exists but is empty/cleared, respect that and use text input
    const hasUnitOptionsKey = "_unitOptions" in columnData;
    if (!hasUnitOptionsKey) {
      // _unitOptions was never set, check SKU data directly (edit mode scenario)
      const skuCode = columnData["sku_code"];
      if (
        skuCode &&
        typeof skuCode === "object" &&
        "alternateStockUnit" in skuCode &&
        skuCode.alternateStockUnit &&
        (skuCode as any).alternateStockUnit?.alternate_stock_unit_name
      ) {
        // SKU has alternate units, render as dropdown
        return "json";
      }
    }

    // Fallback to text input if no multiple units are available
    return "text";
  }

  // Add more column-specific logic here as needed
  // Example:
  // if (column.column_name === "another_column") {
  //   const options = getPreStoredOptions(columnData, "_anotherColumnOptions");
  //   if (options) return "json";
  // }

  // Default to the column's original data_type
  return column.data_type;
}

export const removeDuplicate = (data: any) => {
  const nonRepeatData: any = new Set();

  data?.forEach(
    (obj: any) =>
      !nonRepeatData.has(JSON.stringify(obj)) &&
      nonRepeatData.add(JSON.stringify(obj))
  );

  const finalData: any = new Set(
    [...nonRepeatData].map((item) => JSON.parse(item))
  );
  return finalData;
};

export const setIGSTAmount = ({
  amount,
  gstPercentage,
  skipGstCalculation,
  isInterStateTransaction,
  handleColumnValueChange,
  discount,
}: AutoCalculationType) => {
  if (!amount) {
    handleColumnValueChange("igst_amount", "");
    return;
  }

  const rawValue = +amount - +discount;
  const taxableValue = rawValue ? rawValue.toFixed(2) : "";

  if (skipGstCalculation || !isInterStateTransaction) {
    handleColumnValueChange("igst_amount", "");
    return;
  }

  const rawIGST = (+taxableValue * +gstPercentage) / 100;
  const finalIGSTAmount = rawIGST ? rawIGST.toFixed(2) : "";
  handleColumnValueChange("igst_amount", finalIGSTAmount);
};

export const setCGSTAndSGSTAmount = ({
  amount,
  gstPercentage,
  skipGstCalculation,
  isInterStateTransaction,
  handleColumnValueChange,
  discount,
  columnName,
}: AutoCalculationType & { columnName: "sgst_amount" | "cgst_amount" }) => {
  if (!amount) {
    handleColumnValueChange(columnName, "");
    return;
  }

  const rawValue = +amount - +discount;
  const taxableValue = rawValue ? rawValue.toFixed(2) : "";

  if (skipGstCalculation || isInterStateTransaction) {
    handleColumnValueChange(columnName, "");
    return;
  }

  const rawSGST = (+taxableValue * +gstPercentage) / 200;
  const finalIGSTAmount = rawSGST ? rawSGST.toFixed(2) : "";
  handleColumnValueChange(columnName, finalIGSTAmount);
};

export const setTotalValue = ({
  skipGstCalculation,
  amount,
  gstPercentage,
  discount,
  handleColumnValueChange,
}: AutoCalculationType) => {
  const amt = +amount || 0;
  const disc = +discount || 0;
  const gst = +gstPercentage || 0;

  let taxableValue = amt - disc;
  taxableValue = +taxableValue.toFixed(2);

  let totalValue = taxableValue;

  // Apply GST if applicable
  if (gst && !skipGstCalculation) {
    const gstTotal = (taxableValue * gst) / 100;
    totalValue = taxableValue + gstTotal;
  }

  handleColumnValueChange(
    "total_value",
    totalValue ? totalValue.toFixed(2) : ""
  );
};

export function checkRequiredColumns({
  columnList,
  columnData,
  handleSetError,
}: {
  columnList: LinkedSingleColumnType[];
  handleSetError: (key: string, value: string) => void;
  columnData: Record<string, valueType>;
}) {
  let validationFail = false;
  columnList.map((column) => {
    const columnValue = columnData[column.column_name];
    if (
      column.validation.includes("required") &&
      (columnValue === "" || columnValue === undefined || columnValue === null)
    ) {
      handleSetError(column.column_name, `${column.name} is required`);
      validationFail = true;
    }
  });
  return validationFail;
}

export function calculateAmount(rate: number, itemTotalValueSum: number) {
  if (!rate || (typeof rate !== "string" && typeof rate !== "number")) {
    return 0;
  }

  return (itemTotalValueSum * (+rate / 100)).toFixed(2);
}
function getInvoiceCategory(
  tabType: InvoiceType,
  documentType: valueType,
  initialState:
    | InitialStateInvoicingAdvanceType["singleInvoiceDetails"]
    | undefined
) {
  const currentCategory = initialState?.invoice_category;
  const docValue = isLabeledValue(documentType) ? documentType.value : "";

  // ----- EXPENSES -----
  // If document type = purchase_order → category 2
  if (tabType === "expenses" && docValue === "purchase_order") {
    return 2;
  }

  // If current = 2 and doc type changed from purchase_order → category 3
  if (
    tabType === "expenses" &&
    currentCategory === 2 &&
    documentType &&
    docValue !== "purchase_order"
  ) {
    return 3;
  }

  // If current already = 3 → stay 3
  if (tabType === "expenses" && currentCategory === 3) {
    return 3;
  }

  // ----- REVENUES -----
  // If document type = estimation → category 2
  if (tabType === "revenues" && docValue === "estimation") {
    return 2;
  }

  // If current = 2 and doc type changed from estimation → category 3
  if (
    tabType === "revenues" &&
    currentCategory === 2 &&
    docValue !== "estimation"
  ) {
    return 3;
  }

  // If current already = 3 → stay 3
  if (tabType === "revenues" && currentCategory === 3) {
    return 3;
  }

  // Defaults
  return 1;
}

export function calculateTDSTCSAmountFromItems(
  items: Record<string, valueType>[],
  rate: number | null
) {
  if (!rate || !Array.isArray(items)) return 0;
  const { itemTaxableValueSum } = items.reduce(
    (acc, item) => {
      const taxableValue =
        typeof item["taxable_value"] === "string" ||
        typeof item["taxable_value"] === "number"
          ? +item["taxable_value"]
          : 0;

      return {
        itemTaxableValueSum: +acc.itemTaxableValueSum + +taxableValue,
      };
    },
    { itemTaxableValueSum: 0 }
  );
  return calculateAmount(rate, itemTaxableValueSum);
}

export function calculateFinalTotalAmount(
  TDSTCSDetail: TDSTCSDetailType,
  itemTotalValueSum: number
) {
  if (!TDSTCSDetail.amount) {
    return itemTotalValueSum;
  }

  if (TDSTCSDetail.type === "tds") {
    return (itemTotalValueSum - TDSTCSDetail.amount).toFixed(2);
  } else {
    return (itemTotalValueSum + TDSTCSDetail.amount).toFixed(2);
  }
}

export function convertRawDataToAddInvoiceApiPayload({
  extendedColumnListItem,
  extendedColumnListPayment,
  extendedColumnListPrimary,
  TDSTCSDetail,
  tabType,
  clientCode,
  isInvoiceOCRCreated,
  isInvoiceGSTRCreated,
  gstSource,
  form,
  mode,
  initialState,
  currencyExchangeDetails,
  isServiceInvoice,
}: {
  extendedColumnListItem: LinkedSingleColumnType[];
  extendedColumnListPayment: LinkedSingleColumnType[];
  extendedColumnListPrimary: LinkedSingleColumnType[];
  TDSTCSDetail: TDSTCSDetailType;
  tabType: null | InvoiceType;
  clientCode: null | number;
  isInvoiceOCRCreated: number;
  isInvoiceGSTRCreated: number;
  gstSource?: string | null;
  form: FormType;
  mode: InvoicingAdvanceMode;
  initialState:
    | InitialStateInvoicingAdvanceType["singleInvoiceDetails"]
    | undefined;
  currencyExchangeDetails: CurrencyExchangeDetailsType;
  isServiceInvoice?: boolean;
}) {
  if (!tabType || !clientCode) {
    return null;
  }

  const totalValue = form.itemDetails.reduce((acc, item) => {
    const value = item["total_value"];
    if (typeof value === "string" || typeof value === "number") {
      return acc + Number(value);
    }
    return acc;
  }, 0);

  const netAmount = calculateFinalTotalAmount(TDSTCSDetail, totalValue);

  let customerSupplierChanged = 0;
  if (mode === "edit") {
    const columnName =
      tabType === "expenses" ? "supplier_name" : "customer_name";

    const prevCustomerSupplierColumn = initialState?.invoice_data?.primary.find(
      (col) => col.column_name === columnName
    );

    const newCustomerSupplier = form.primaryDetails[columnName];

    if (
      prevCustomerSupplierColumn?.column_value &&
      typeof prevCustomerSupplierColumn.column_value === "object" &&
      "ledger_id" in prevCustomerSupplierColumn.column_value &&
      isLabeledValue(newCustomerSupplier)
    ) {
      const prevLedgerId = String(
        prevCustomerSupplierColumn.column_value.ledger_id
      );
      const newLedgerId = newCustomerSupplier.value;

      if (prevLedgerId != newLedgerId) {
        customerSupplierChanged = 1;
      }
    }
  }

  const payload = {
    invoice_date: moment(
      form.primaryDetails["voucher_invoice_date"] as Date
    ).format("YYYY-MM-DD"),
    type: tabType,
    client_code: +clientCode,
    is_invoice_ocr_created: isInvoiceOCRCreated,
    ...(mode === "edit"
      ? { is_customer_supplier_changed: customerSupplierChanged }
      : {}),
    is_invoice_gstr2a2b_created: isInvoiceGSTRCreated,
    gstr_source: gstSource,
    is_service_invoice: isServiceInvoice ?? false,
    ocr_session_id: isInvoiceOCRCreated
      ? (initialState?.ocr_session_id ?? null)
      : null,
    rate: currencyExchangeDetails?.rate || 1,
    invoice_category: getInvoiceCategory(
      tabType,
      form.primaryDetails["document_type"],
      initialState
    ),
    invoice_data: {
      tds: TDSTCSDetail.amount,
      tds_tcs_details: {
        ...TDSTCSDetail,
        section_name: TDSTCSDetail.sectionName,
      },

      invoice_value: totalValue,
      net_amount: netAmount,
      primary: createPrimaryDataForPayload(
        form.primaryDetails,
        extendedColumnListPrimary
      ),
      item: createItemDataForPayload(
        form.itemDetails,
        extendedColumnListItem,
        mode,
        initialState
      ),
      payment: createPaymentDataForPayload(
        form.paymentDetails,
        extendedColumnListPayment,
        mode,
        initialState
      ),
    },
  };
  return payload;
}

export function createPrimaryDataForPayload(
  primaryData: Record<string, valueType>,
  extendedColumnListPrimary: LinkedSingleColumnType[]
) {
  return extendedColumnListPrimary.map((column) => {
    return {
      id: column.id,
      name: column.name,
      column_name: column.column_name,
      data_type: column.data_type,
      sequence: column.sequence,
      column_value: (function () {
        const value = primaryData[column.column_name] ?? "";

        if (
          (column.column_name === "branch" ||
            column.column_name === "bill_from") &&
          isLabeledValue(value)
        ) {
          const strArr = value.label.split("%");

          return {
            ledger_id: value.value,
            legal_name: strArr[1],
            state: strArr.at(-1),
            trade_name: strArr.at(0),
          };
        }

        if (
          (column.column_name === "supplier_name" ||
            column.column_name === "customer_name") &&
          isLabeledValue(value)
        ) {
          const strArr = value.label.split("%");

          return {
            ledger_id: value.value,
            legal_name: strArr[0],
            state: strArr.at(-1),
            trade_name: strArr.at(-2),
          };
        }

        if (
          column.column_name === "mapping_tax_invoice" &&
          isLabeledValue(value) &&
          value.value
        ) {
          const strArr = value.label.split("%");
          return {
            id: value.value,
            voucher_invoice_number: strArr.at(-1),
            voucher_invoice_date: strArr[0],
            net_amount: strArr[1],
          };
        }

        if (column.column_name === "ship_to_name" && isLabeledValue(value)) {
          const strArr = value.label.split("%");
          return {
            shipping_id: value.value,
            legal_name: strArr[0],
          };
        }

        if (
          column.column_name === "currency" &&
          typeof value === "object" &&
          "key" in value
        ) {
          return {
            value: value?.value,
            label: value.label,
            currencyShortForm: value?.currencyShortForm,
            currencySign: value?.currencySign,
          };
        }

        if (
          column.data_type === "date" &&
          (isDate(value) || (value && typeof value === "string"))
        ) {
          return moment(value).format("DD-MM-YYYY");
        }

        if (
          column.column_name === "voucher_invoice_number" &&
          typeof value === "string"
        ) {
          return value?.trim();
        }

        return value;
      })(),
    };
  });
}

export function createItemDataForPayload(
  itemData: Record<string, valueType>[],
  extendedColumnListItem: LinkedSingleColumnType[],
  mode: InvoicingAdvanceMode,
  initialState:
    | InitialStateInvoicingAdvanceType["singleInvoiceDetails"]
    | undefined
) {
  const itemDetail = itemData.map((item) => {
    return extendedColumnListItem.map((column) => {
      return {
        id: column.id,
        name: column.name,
        column_name: column.column_name,
        data_type: column.data_type,
        sequence: column.sequence,
        column_value: (function () {
          const value = item[column.column_name] ?? "";

          // "NA" means no SKU, so send it as empty
          if (column.column_name === "sku_code" && isSkuNA(value)) {
            return "";
          }

          if (
            (column.column_name === "nature_of_expense" ||
              column.column_name === "nature_of_income") &&
            isLabeledValue(value)
          ) {
            const strArr = value.label.split("%");
            const ledgerName = strArr[0];
            const ledgerNumber = strArr[1];
            const ledgerType = strArr[2];
            const ledgerId = value.value;
            return {
              ledger_id: +ledgerId,
              ledger_name: ledgerName,
              ledger_number: ledgerNumber,
              ledger_type: ledgerType,
            };
          }

          if (column.column_name === "batch_number" && isLabeledValue(value)) {
            const strArr = value.label.split("%");
            const batchNumber = strArr[0];
            const expiryDate = strArr[1];
            const manufacturingDate = strArr[2];
            return {
              value: value.value,
              label: batchNumber,
              manufacturing_date: manufacturingDate,
              expiry_date: expiryDate,
            };
          }

          // Handle unit column when it's a LabeledValue (from dropdown with multiple units)
          if (column.column_name === "unit" && isLabeledValue(value)) {
            // Extract the unit value (string) from LabeledValue
            return typeof value.value === "string" ? value.value : value.label;
          }

          return value;
        })(),
      };
    });
  });

  if (mode === "edit" && initialState?.invoice_data?.item) {
    const itemKeys = Object.keys(initialState.invoice_data.item) as (
      | string
      | number
    )[];

    let newKeyCounter = 0;

    return itemDetail.reduce(
      (acc, data, index) => {
        const key = itemKeys[index] ?? `new-${++newKeyCounter}`;
        acc[key as string | number] = data as SingleColumnTypeWithValue[];
        return acc;
      },
      {} as Record<string | number, SingleColumnTypeWithValue[]>
    );
  }

  return itemDetail;
}

export function createPaymentDataForPayload(
  paymentData: Record<string, valueType>[],
  extendedColumnListPayment: LinkedSingleColumnType[],
  mode: InvoicingAdvanceMode,
  initialState:
    | InitialStateInvoicingAdvanceType["singleInvoiceDetails"]
    | undefined
) {
  const paymentDetails = paymentData.map((item) => {
    return extendedColumnListPayment.map((column) => {
      return {
        id: column.id,
        name: column.name,
        column_name: column.column_name,
        data_type: column.data_type,
        sequence: column.sequence,
        column_value: (function () {
          const value = item[column.column_name] ?? "";

          const { column_name: columnName } = column;
          if (
            (columnName === "mode_of_payment" ||
              columnName === "mode_of_receipts") &&
            isLabeledValue(value)
          ) {
            const strArr = value.label.split("%");
            const ledgerName = strArr[0];
            const ledgerNumber = strArr[1];
            const ledgerType = strArr.at(-1);
            const ledgerId = value.value;
            return {
              ledger_id: +ledgerId,
              ledger_name: ledgerName,
              ledger_number: ledgerNumber,
              ledger_type: ledgerType,
            };
          }

          if (
            column.data_type === "date" &&
            (isDate(value) || (value && typeof value === "string"))
          ) {
            return moment(value).format("DD-MM-YYYY");
          }

          return value;
        })(),
      };
    });
  });
  if (mode === "edit" && initialState?.invoice_data?.payment) {
    const paymentKeys = Object.keys(initialState.invoice_data.payment) as (
      | string
      | number
    )[];
    const result: Record<number, any> = {};
    paymentData.forEach((data, index) => {
      const creditNoteValue = data["credit_note_id"] ?? "";
      const allocationValue = data["allocation_id"] ?? "";
      const otherInvoiceId = data["other_invoice_id"] ?? "";
      const extras = [];

      extras.push({
        id: 401,
        name: "Credit Note id",
        column_name: "credit_note_id",
        data_type: "int",
        sequence: 401,
        column_value: creditNoteValue,
      });

      extras.push({
        id: 400,
        name: "Allocation id",
        column_name: "allocation_id",
        data_type: "int",
        sequence: 400,
        column_value: allocationValue,
      });

      extras.push({
        id: 402,
        name: "Others Invoice id",
        column_name: "other_invoice_id",
        data_type: "int",
        sequence: 402,
        column_value: otherInvoiceId,
      });

      result[index] = [...paymentDetails[index], ...extras];
    });
    let newKeyCounter = 0;
    return Object.keys(result).reduce(
      (acc, _, index) => {
        const key = paymentKeys[index] ?? `new-${++newKeyCounter}`;
        acc[key] = result[index];
        return acc;
      },
      {} as Record<string | number, SingleColumnTypeWithValue[]>
    );
  }

  return paymentDetails;
}

export function createFormInitialState(
  initialState:
    | InitialStateInvoicingAdvanceType["singleInvoiceDetails"]
    | undefined,
  currency: CurrencyType,
  documentTypeInitialState: LabeledValue | undefined
) {
  const fetchers = createFetchers();

  return {
    primaryDetails: createPrimaryDataInitialState(
      initialState,
      currency,
      fetchers,
      documentTypeInitialState
    ),
    itemDetails: createItemInitialState(initialState, fetchers),
    paymentDetails: createPaymentInitialState(initialState, fetchers),
  };
}

function createPrimaryDataInitialState(
  initialState:
    | InitialStateInvoicingAdvanceType["singleInvoiceDetails"]
    | undefined,
  currency: CurrencyType,
  fetchers: ExtendedFetchers,
  documentTypeInitialState: LabeledValue | undefined
) {
  const primaryData = initialState?.invoice_data?.primary;
  const invoiceCurrency = primaryData?.find(
    (col) => col.column_name === "currency"
  )?.column_value;
  const clientCurrency =
    currency.currencyName && currency.currencyShortForm && currency.currencySign
      ? {
          key: "currency",
          value: currency.currencyName,
          label: `${currency.currencyName}(${currency.currencyShortForm})`,
          currencyShortForm: currency.currencyShortForm,
          currencySign: currency.currencySign,
        }
      : "";

  const initialPrimaryData: Record<string, valueType> = {
    document_type: documentTypeInitialState
      ? documentTypeInitialState
      : {
          value: "tax_invoice",
          label: "Tax Invoice",
        },
    currency:
      invoiceCurrency && isLabeledValue(invoiceCurrency)
        ? { key: "currency", ...invoiceCurrency }
        : clientCurrency,
  };

  if (Array.isArray(primaryData) && primaryData.length > 0) {
    primaryData.forEach((column) => {
      let value = column.column_value ?? "";

      if (
        column.data_type === "json" &&
        value &&
        COLUMNS_USING_TO_OPTION_INITIAL.includes(column.column_name)
      ) {
        const fetcher = fetchers[column.column_name as keyof ExtendedFetchers];

        if (fetcher && fetcher.columnName === column.column_name) {
          value = fetcher.toOption(value as any);
        }
      }

      if (
        (column.column_name === "currency" ||
          column.column_name === "document_type") &&
        !value
      ) {
        return;
      }
      initialPrimaryData[column.column_name] = value as valueType;
    });
  }
  return initialPrimaryData;
}

function createItemInitialState(
  initialState:
    | InitialStateInvoicingAdvanceType["singleInvoiceDetails"]
    | undefined,
  fetchers: ExtendedFetchers
) {
  const itemData = initialState?.invoice_data?.item;

  const initialItemData: Record<string, valueType>[] = [];

  if (itemData && typeof itemData === "object") {
    Object.values(itemData).forEach((columnsArray) => {
      const singleItem: Record<string, valueType> = {};

      columnsArray.forEach((col) => {
        const fetcher = fetchers[col.column_name as keyof ExtendedFetchers];
        let value = (col.column_value ?? "") as valueType;

        if (
          col.data_type === "json" &&
          fetcher &&
          fetcher.columnName === col.column_name &&
          value &&
          COLUMNS_USING_TO_OPTION_INITIAL.includes(col.column_name)
        ) {
          value = fetcher.toOption(col.column_value as any);
        }
        singleItem[col.column_name] = value as valueType;
      });

      // currencey conversion logic starts heree

      // Find the currency conversion object if present and cast to a record so it can be indexed by string
      const currencyDetails = columnsArray.find(
        (col) => col.column_name === "currency_amount_details"
      )?.column_value as Record<string, valueType> | undefined;

      if (currencyDetails) {
        Object.entries(currencyDetails).forEach(([key, val]) => {
          singleItem[key] = val as valueType;
        });
      }

      // currency conversion logic ends here

      initialItemData.push(singleItem);
    });
  }

  return initialItemData;
}

function createPaymentInitialState(
  initialState:
    | InitialStateInvoicingAdvanceType["singleInvoiceDetails"]
    | undefined,
  fetchers: ExtendedFetchers
) {
  const paymentData = initialState?.invoice_data?.payment;

  const initialPaymentData: Record<string, valueType>[] = [];

  if (paymentData && typeof paymentData === "object") {
    Object.values(paymentData).forEach((columnsArray) => {
      const singlePayment: Record<string, valueType> = {};

      columnsArray.forEach((col) => {
        let value = (col.column_value || "") as valueType;
        const fetcher = fetchers[col.column_name as keyof ExtendedFetchers];

        if (
          col.data_type === "json" &&
          fetcher &&
          fetcher.columnName === col.column_name &&
          value &&
          COLUMNS_USING_TO_OPTION_INITIAL.includes(col.column_name)
        ) {
          value = fetcher.toOption(col.column_value as any);
        }
        singlePayment[col.column_name] = value as valueType;
      });

      initialPaymentData.push(singlePayment);
    });
  }

  return initialPaymentData;
}

export function createTDSTCSInitialState(
  initialState: TDSTCSDetailApiReturnType | undefined,
  currencyAmountDetails:
    | {
        invoice_value: number;
        tds: number;
        net_amount: number;
        tds_tcs_amount: number;
      }
    | undefined
) {
  const TDSTCSInitialState: TDSTCSDetailType = {
    amount: null,
    id: null,
    rate: null,
    sectionName: "",
    type: "tds",
  };
  if (initialState && typeof initialState === "object") {
    return {
      amount: currencyAmountDetails?.tds_tcs_amount
        ? +currencyAmountDetails?.tds_tcs_amount
        : (initialState.amount ?? null),
      id: initialState.id ?? 0,
      rate: initialState.rate ?? null,
      sectionName: initialState.details?.section_name ?? "",
      type: initialState.type ?? "tds",
    };
  }
  return TDSTCSInitialState;
}

const makeBusinessAddressFetcher = () =>
  ({
    columnName: "branch",
    isInfinite: false,
    request: async ({ token, clientCode, searchTerm }) =>
      getAllBusinessAddressesApi({
        token,
        params: {
          client_code: clientCode,
          search_term: searchTerm ?? "",
        },
      }),
    toOption: (item): LabeledValue => ({
      label: `${item?.trade_name ?? ""}%${item?.legal_name ?? ""}%${item.state}`,
      value: item?.ledger_id ? String(item.ledger_id) : "",
      isServiceInvoice: !!item?.is_service_invoice,
    }),
  }) as ExtendedFetchers["branch"];

const makeCustomerSupplierFetcher = () =>
  ({
    columnName: "supplier_name",
    isInfinite: true,
    request: async ({ token, clientCode, searchTerm, limit, offset }) =>
      getAllCustomerSupplierApi({
        token,
        params: {
          client_code: clientCode,
          limit: limit ?? 20,
          offset: offset ?? 0,
          to_show: 1,
          data_req: "enabled",
          search_term: searchTerm ?? "",
        },
      }),
    // in every toOption changing the order of the keys will impact multiple things
    toOption: (item): LabeledValue => ({
      label: `${item.legal_name ?? ""}%${item.ledger_number ?? ""}%${item.trade_name ?? ""}%${item.state ?? ""}`,
      value: item?.ledger_id ? String(item.ledger_id) : "",
    }),
  }) as ExtendedFetchers["supplier_name"];

const makeDocumentType = () =>
  ({
    columnName: "document_type",
    isInfinite: false,
    request: async ({ token, clientCode, invoiceType, mode }) =>
      getDocumentTypeListingApi({
        token,
        clientCode,
        invoice_type: invoiceType,
      }),
    toOption: (item): LabeledValue => ({
      label: item?.label,
      value: item?.value,
      subValue: item?.alias_name ? item?.alias_name : "",
    }),
  }) as ExtendedFetchers["document_type"];

const makeAccountNatureFetcher = () =>
  ({
    columnName: "nature_of_expense",
    isInfinite: false,
    request: async ({ token, clientCode }) =>
      getAllLedgersListApi({
        token,
        params: {
          client_code: clientCode,
          data_req: "enabled",
        },
      }),
    toOption: (item): LabeledValue => ({
      label: `${item.ledger_name ?? ""}%${item.ledger_number ?? ""}%${item.ledger_type}`,
      value: item?.ledger_id ? String(item.ledger_id) : "",
    }),
  }) as ExtendedFetchers["nature_of_expense"];

const makePaymentModeFetcher = () =>
  ({
    columnName: "mode_of_payment",
    isInfinite: false,
    request: async ({ token, clientCode, data_req, tabType }) => {
      if (tabType === "others") {
        return getAllTypesWithTaxListApi({
          token,
          client_code: clientCode,
          data_req,
        });
      }
      return getAllTypesLedgerListApi({
        token,
        client_code: clientCode,
        data_req,
      });
    },
    toOption: (item): LabeledValue => ({
      label: `${item.ledger_name ?? ""}%${item.ledger_number ?? ""}%${item.trade_name ?? item.legal_name ?? ""}%${item.ledger_type_id ?? ""}%${item.ledger_type ?? ""}`,
      value: item?.ledger_id ? String(item.ledger_id) : "",
    }),
  }) as ExtendedFetchers["mode_of_payment"];

export const createFetchers = (
  singleCustomerSupplierAddress?: ShippingAddresses[],
  invoiceDate?: string | null
) => {
  const fetchers: ExtendedFetchers = {
    branch: makeBusinessAddressFetcher(),
    bill_from: { ...makeBusinessAddressFetcher(), columnName: "bill_from" },
    supplier_name: makeCustomerSupplierFetcher(),
    customer_name: {
      ...makeCustomerSupplierFetcher(),
      columnName: "customer_name",
    },
    place_of_supply: {
      columnName: "place_of_supply",
      isInfinite: false,
      request: () => {
        return STATES_LIST_WITH_CODE;
      },
      toOption: (item): LabeledValue => ({
        label: item.label,
        value: item.value,
      }),
    },
    document_type: makeDocumentType(),
    mapping_tax_invoice: {
      columnName: "mapping_tax_invoice",
      isInfinite: false,
      request: async ({ token, clientCode, type, ledger_id }) =>
        getInvoicingAdvanceNotesListApi({
          token,
          params: {
            client_code: clientCode,
            type,
            ledger_id,
          },
        }),
      toOption: (item): LabeledValue => ({
        label: `${item.voucher_invoice_date ?? ""}%${item.net_amount ?? ""}%${item.voucher_invoice_number ?? ""}`,
        value: item?.id ? String(item.id) : "",
      }),
    },
    ship_to_name: {
      columnName: "ship_to_name",
      isInfinite: false,
      request: () => {
        return singleCustomerSupplierAddress!;
      },
      toOption: (item): LabeledValue => ({
        label: item.legal_name ?? "",
        value: item.shipping_id ? String(item.shipping_id) : "",
      }),
    },
    nature_of_expense: makeAccountNatureFetcher(),
    nature_of_income: {
      ...makeAccountNatureFetcher(),
      columnName: "nature_of_income",
    },
    department: {
      columnName: "department",
      isInfinite: true,
      request: async ({ token, status, searchTerm, limit, offset }) =>
        getMasterDataUsersDepartmentApi({
          token,
          limit,
          offset,
          status,
          ...(searchTerm ? { searchValue: searchTerm } : {}),
        }),
      toOption: (item): LabeledValue => ({
        label: `${item.department_name ?? ""}`,
        value: item?._id,
      }),
    },
    sku_code: {
      columnName: "sku_code",
      isInfinite: true,
      request: async ({ token, clientCode, limit, offset, searchTerm }) =>
        getAllStockItemListApi({
          token,
          client_code: clientCode,
          limit,
          offset,
          ...(searchTerm ? { search_term: searchTerm } : {}),
        }),
      toOption: (item): LabeledValue => ({
        label: `${item.stock_item_name ?? ""}`,
        value: item?.stock_item_id ? String(item.stock_item_id) : "",
        unitConversion: item?.unit_conversion_factor ?? "",
        alternateStockUnit: item?.alternate_stock_unit
          ? item?.alternate_stock_unit
          : undefined,
        primaryUnitName: item?.stock_unit_name ?? "",
      }),
    },
    batch_number: {
      columnName: "batch_number",
      isInfinite: false,
      request: async ({ token, clientCode, stockId, isEnable }) =>
        getStockMappedBatchListApi({
          token,
          clientCode,
          stockId,
          isEnable,
        }),
      toOption: (item): LabeledValue => ({
        label: `${item.batch_number ?? item?.label ?? ""}%${item.expiry_date ?? ""}%${item.manufacturing_date}%`,
        value: item?.batch_id ? String(item.batch_id) : (item?.value ?? ""),
      }),
    },
    mode_of_payment: makePaymentModeFetcher(),
    mode_of_receipts: {
      ...makePaymentModeFetcher(),
      columnName: "mode_of_receipts",
    },
    TDS_DropDown: {
      columnName: "TDS_DropDown",
      isInfinite: true,
      request: async ({ token, limit, offset, searchTerm }) =>
        getMasterTdsModuleApi({
          token,
          limit,
          offset,
          searchValue: searchTerm,
        }),
      toOption: (item): LabeledValue => {
        const rate = pickRateByDate(
          (item.tds_rates ?? []).map(
            (r: { tds_date: string; tds_rate: number }) => ({
              date: r.tds_date,
              rate: r.tds_rate,
            })
          ),
          invoiceDate
        );
        return {
          label: `${item.section_name ?? ""}%${rate}%${item.nature_of_payment}%${item.code}`,
          value: item?.tds_id ? String(item.tds_id) : "",
        };
      },
    },
    TCS_DropDown: {
      columnName: "TCS_DropDown",
      isInfinite: true,
      request: async ({ token, limit, offset, searchTerm }) =>
        getMasterTcsModuleApi({
          token,
          limit,
          offset,
          searchValue: searchTerm,
        }),
      toOption: (item): LabeledValue => {
        const rate = pickRateByDate(
          (item.tcs_rates ?? []).map(
            (r: { tcs_date: string; tcs_rate: number }) => ({
              date: r.tcs_date,
              rate: r.tcs_rate,
            })
          ),
          invoiceDate
        );
        return {
          label: `${item.section_name ?? ""}%${rate}%${item.nature_of_payment}%${item.code}`,
          value: item?.tcs_id ? String(item.tcs_id) : "",
        };
      },
    },
    currency: {
      columnName: "currency",
      isInfinite: true,
      request: async ({ token, limit, offset, searchTerm, clientCode }) =>
        getAllMasterDataCurrencyListApi({
          token,
          limit,
          offset,
          search_term: searchTerm,
          client_code: clientCode,
        }),
      toOption: (item): LabeledValue => ({
        label: `${item.currency_name ?? ""}%${item.currency_short_form ?? ""}%${item.currency_sign}`,
        value: item.currency_name,
      }),
    },
    ["sent/received"]: {
      columnName: "sent/received",
      isInfinite: false,
      request: () => {
        return TRANSACTION_TYPES;
      },
      toOption: (item): LabeledValue => ({
        label: item.label,
        value: item.value,
      }),
    },
  };
  return fetchers;
};

export function normalizeDateValue(rawValue: unknown): Date | null {
  if (rawValue instanceof Date) return rawValue;

  if (typeof rawValue === "string") {
    const parsed = moment(rawValue, ["YYYY-MM-DD", "DD-MM-YYYY"]);
    if (parsed.isValid()) return parsed.toDate();
  }

  return null;
}

export const handleHeaderDescriptionWidth = (num: number) => {
  switch (num) {
    case 3:
      return "80%";
    case 4:
      return "70%";
    case 5:
      return "60%";
    case 6:
      return "55%";
    case 7:
      return "58%";
    default:
      return "35%";
  }
};

export const handleOthersWidth = (num: number) => {
  switch (num) {
    case 3:
      return "20%";
    case 4:
      return "18%";
    case 5:
      return "15%";
    case 6:
      return "14%";
    case 7:
      return "13%";
    default:
      return "12%";
  }
};

export const handleGstSummaryWidth = (num: number) => {
  switch (num) {
    case 4:
      return "25%";
    case 5:
      return "20%";
    default:
      return "25%";
  }
};

export const getMarginValue = (length: number): number => {
  switch (true) {
    case length === 13:
      return 92;
    case length === 12:
      return 75;
    case length === 11:
      return 60;
    case length === 10:
      return 50;
    case length === 9:
      return 35;
    case length === 8:
      return 12;
    case length === 4:
      return 35;
    case length === 5:
      return 28;
    case length === 6:
      return 40;
    case length === 7:
      return 70;
    default:
      return 20;
  }
};

export const getBatchMarginValue = (length: number): number => {
  switch (true) {
    case length === 13:
      return 40;

    default:
      return 22;
  }
};

export const checkingForHeaders = (
  data: any,
  tableHeaders: any,
  type?: string
) => {
  let revenueTableHeaderData = [...tableHeaders];

  let shouldIncludeSkuCode = false;
  let shouldIncludeIgstAmount = false;
  let shouldIncludeCgstAmount = false;
  let shouldIncludeSgstAmount = false;
  let shouldIncludeGst = false;
  let shouldIncludeDiscount = false;
  let shouldIncludeHsnSac = false;
  let shouldIncludeRate = false;
  let shouldIncludeQuantity = false;
  let shouldIncludeAmount = false;
  let shouldIncludeTaxable = false;
  let shouldIncludeTotalValue = false;
  let shouldIncludeBatchSummary = false;

  data?.forEach((singleItem: any) => {
    if (!!singleItem?.sku_code && type != "withoutSku")
      shouldIncludeSkuCode = true;
    if (+singleItem?.igst_amount > 0) shouldIncludeIgstAmount = true;
    if (+singleItem?.cgst_amount > 0) shouldIncludeCgstAmount = true;
    if (+singleItem?.sgst_amount > 0) shouldIncludeSgstAmount = true;
    if (+singleItem?.gst_percentage > 0) shouldIncludeGst = true;
    if (+singleItem?.discount > 0) shouldIncludeDiscount = true;
    if (+singleItem?.hsn_sac_code > 0) shouldIncludeHsnSac = true;
    if (+singleItem?.rate > 0) shouldIncludeRate = true;
    if (+singleItem?.quantity > 0) shouldIncludeQuantity = true;
    if (+singleItem?.amount > 0) shouldIncludeAmount = true;
    if (+singleItem?.taxable_value > 0) shouldIncludeTaxable = true;
    if (+singleItem?.total_value > -1) shouldIncludeTotalValue = true;
    if (singleItem?.batch_number?.value > 0) shouldIncludeBatchSummary = true;
  });

  if (!shouldIncludeSkuCode) {
    revenueTableHeaderData = revenueTableHeaderData.filter(
      (item) => item.column_value !== "sku_code"
    );
  }
  if (!shouldIncludeIgstAmount) {
    revenueTableHeaderData = revenueTableHeaderData.filter(
      (item) => item.column_value !== "igst_amount"
    );
  }
  if (!shouldIncludeCgstAmount) {
    revenueTableHeaderData = revenueTableHeaderData.filter(
      (item) => item.column_value !== "cgst_amount"
    );
  }
  if (!shouldIncludeSgstAmount) {
    revenueTableHeaderData = revenueTableHeaderData.filter(
      (item) => item.column_value !== "sgst_amount"
    );
  }
  if (!shouldIncludeGst) {
    revenueTableHeaderData = revenueTableHeaderData.filter(
      (item) =>
        item.column_value !== "gst_percentage" &&
        item.column_value !== "cgst_amount" &&
        item.column_value !== "sgst_amount" &&
        item.column_value !== "igst_amount"
    );
  }
  if (!shouldIncludeDiscount) {
    revenueTableHeaderData = revenueTableHeaderData.filter(
      (item) => item.column_value !== "discount"
    );
  }
  if (!shouldIncludeHsnSac) {
    revenueTableHeaderData = revenueTableHeaderData.filter(
      (item) => item.column_value !== "hsn_sac_code"
    );
  }
  if (!shouldIncludeRate) {
    revenueTableHeaderData = revenueTableHeaderData.filter(
      (item) => item.column_value !== "rate"
    );
  }
  if (!shouldIncludeQuantity) {
    revenueTableHeaderData = revenueTableHeaderData.filter(
      (item) => item.column_value !== "quantity"
    );
  }
  if (!shouldIncludeAmount) {
    revenueTableHeaderData = revenueTableHeaderData.filter(
      (item) => item.column_value !== "amount"
    );
  }
  if (!shouldIncludeTaxable) {
    revenueTableHeaderData = revenueTableHeaderData.filter(
      (item) => item.column_value !== "taxable_value"
    );
  }
  if (!shouldIncludeTotalValue) {
    revenueTableHeaderData = revenueTableHeaderData.filter(
      (item) => item.column_value !== "total_value"
    );
  }
  if (!shouldIncludeBatchSummary) {
    revenueTableHeaderData = revenueTableHeaderData.filter(
      (item) => item.column_value !== "batch_id"
    );
  }

  return revenueTableHeaderData;
};

export const checkingForGstSummaryHeaders = (
  data: any,
  tableHeaders: SinglePdfHeaderType[]
) => {
  let shouldIncludeIgstAmount = false;
  let gstSummaryHeaders = [...tableHeaders];

  data?.forEach((singleItem: any) => {
    if (+singleItem?.total_igst_amount > 0) shouldIncludeIgstAmount = true;
  });

  if (shouldIncludeIgstAmount) {
    gstSummaryHeaders = gstSummaryHeaders.filter(
      (item) =>
        item.column_value !== "total_cgst_amount" &&
        item.column_value !== "total_sgst_amount"
    );
  } else {
    gstSummaryHeaders = gstSummaryHeaders.filter(
      (item) => item.column_value !== "total_igst_amount"
    );
  }

  return gstSummaryHeaders;
};

export const renderDocumentType = (label: string | undefined) => {
  if (
    label === "Invoice" ||
    label === "Tax Invoice RCM applicable" ||
    label === "Tax Invoice"
  ) {
    return "Invoice";
  }
  return `${label}`;
};

export const changeHeaders = (name: string) => {
  switch (name) {
    case "total_assessable_value":
      return "Total Assessable";
    case "total_cgst_value":
      return "Total CGST";
    case "total_sgst_value":
      return "Total SGST";
    case "total_igst_value":
      return "Total IGST";
    case "taxable_amount":
      return "Taxable Value";
    case "total_discount":
      return "Total Discount";
    case "total_invoice_value":
      return "Total Value";
    case "number_of_item":
      return "Number of Item";
    case "document_type":
      return "Document Type";
    case "document_number":
      return "Voucher/Invoice Number";
    case "document_date":
      return "Voucher/Invoice Date";
    case "address1":
      return "Address 1";
    case "address2":
      return "Address 2";
    case "legal_name":
      return "Legal Name";
    case "trade_name":
      return "Trade Name";
    case "location":
      return "Location";
    case "pincode":
      return "Pin Code";
    case "gstin":
      return "GSTIN";
    case "state_code":
      return "State Code";
    case "email":
      return "Email";
    case "phone_number":
      return "Phone Number";
    case "item_serial_number":
      return "Serial No.";
    case "product_description":
      return "Description as per Bill";
    case "unit":
      return "Unit";
    case "place_of_supply":
      return "Place of Supply";
    case "quantity":
      return "Quantity";
    case "hsn_code":
      return "HSN/SAC Code";
    case "discount":
      return "Discount";
    case "unit_price":
      return "Unit Price";
    case "total_amount":
      return "Total Amount";
    case "assessable_value":
      return "Assessable Value";
    case "gst_rate":
      return "Gst Percentage";
    case "igst_amount":
      return "IGST Amount";
    case "sgst_amount":
      return "SGST Amount";
    case "cgst_amount":
      return "CGST Amount";
    case "total_item_value":
      return "Total Value";
    case "is_service":
      return "Service";
    case "gstin_of_consignor":
      return "GSTIN ";
    case "legal_name_of_consignor":
      return "Legal Name ";
    case "address1_of_consignor":
      return "Address 1";
    case "address2_of_consignor":
      return "Address 2";
    case "place_of_consignor":
      return "City";
    case "pincode_of_consignor":
      return "Pincode";
    case "state_of_consignor":
      return "State";
    case "actual_from_state_name":
      return "Actual From State Name";
    case "gstin_of_consignee":
      return "GSTIN ";
    case "legal_name_of_consignee":
      return "Legal Name ";
    case "address1_of_consignee":
      return "Address 1 ";
    case "address2_of_consignee":
      return "Address 2 ";
    case "place_of_consignee":
      return "City";
    case "pincode_of_consignee":
      return "Pincode";
    case "state_of_supply":
      return "Place of Supply";
    case "actual_to_state_name":
      return "Actual to State Name";
    case "cess_amount":
      return "CESS Amount";
    case "cess_nonadvol_value":
      return "Cess Nonadvol Value";
    case "transporter_id":
      return "Transporter ID";
    case "transporter_name":
      return "Transporter Name";
    case "transporter_document_number":
      return "Transporter Document Number";
    case "transporter_document_date":
      return "Transporter Document Date";
    case "transportation_mode":
      return "Transportation Mode";
    case "transportation_distance":
      return "Transportation Distance";
    case "vehicle_type":
      return "Vehicle Type";
    case "gstin_of_shipping_address":
      return "GSTIN";
    case "legal_name_of_shipping_address":
      return "Legal Name";
    case "address1_of_shipping_address":
      return "Address 1";
    case "address2_of_shipping_address":
      return "Address 2";
    case "place_of_shipping_address":
      return "City";
    case "pincode_of_shipping_address":
      return "Pincode";
    case "state_of_shipping_address":
      return "Place of Supply";
    default:
      return name;
  }
};

export function getHeading(
  mode: InvoicingAdvanceMode,
  invoiceType: InvoiceType
): string {
  const baseMap: Record<InvoiceType, string> = {
    expenses: "Expense",
    revenues: "Sales",
    others: "Others",
    manufacturingJournal: "Manufacturing",
  };

  const modeMap: Record<InvoicingAdvanceMode, string> = {
    add: "Add",
    edit: "Edit",
    copy: "Copy",
    view: "View",
  };

  const base = baseMap[invoiceType];
  const action = modeMap[mode];

  return `${action} ${base} Entry`;
}

export const extractCurrencyInfo = (currencyVal: valueType) => {
  if (!currencyVal) return {};
  if (typeof currencyVal === "object" && "currencyShortForm" in currencyVal) {
    return {
      baseCurrency: currencyVal.currencyShortForm,
      selectedCurrencyName: currencyVal.value,
    };
  }
  if (isLabeledValue(currencyVal)) {
    const [label, shortForm, sign] = currencyVal.label.split("%");
    return {
      baseCurrency: shortForm,
      selectedCurrencyName: currencyVal.value,
      label,
      sign,
    };
  }
  return {};
};

export function checkTypeNumberString(
  value: unknown
): value is string | number {
  return typeof value === "string" || typeof value === "number";
}

export function extractInvoiceFields(primaryData: SingleColumnTypeWithValue[]) {
  const fieldMap = {
    voucher_invoice_number: "voucherInvoiceNumber",
    voucher_invoice_date: "voucherInvoiceDate",
  } as const;

  const result = {
    voucherInvoiceNumber: "",
    voucherInvoiceDate: "",
  };

  primaryData.forEach(({ column_name, column_value }) => {
    const key = fieldMap[column_name as keyof typeof fieldMap];
    if (key) {
      result[key] = checkTypeNumberString(column_value)
        ? String(column_value)
        : "";
    }
  });

  return result;
}

export function checkComparison(item: SingleColumnType) {
  return (
    item?.name !== "Bill From Address1" &&
    item?.name !== "Bill From Address2" &&
    item?.name !== "GST Number" &&
    item?.name !== "Customer Address 1" &&
    item?.name !== "Customer Address 2" &&
    item?.name !== "Customer GSTN (if any)" &&
    item?.name !== "Branch Address1" &&
    item?.name !== "Branch Address2" &&
    item?.name !== "Supplier Address 1" &&
    item?.name !== "Supplier Address 2" &&
    item?.name !== "Supplier GSTN (if any)" &&
    item?.name !== "Supplier Address" &&
    item?.name !== "Customer Address" &&
    item?.name !== "Address" &&
    item?.name !== "Ship To Address" &&
    item?.name !== "Ship To GST (if any)" &&
    item?.name !== "File Upload" &&
    item?.name !== "Ship To Name"
  );
}

export function hasInvoiceNumber(
  invoiceNumber: any
): invoiceNumber is { column_value: string } {
  return typeof invoiceNumber?.column_value === "string";
}

export function transformOthersData(data: InvoicesList) {
  return data.reduce<OthersTabDataType[]>((acc, invoice) => {
    if (!("others_id" in invoice)) return acc;

    acc.push({
      othersId: invoice.others_id,
      dateOfInvoice: invoice.date_of_invoice,
      upload: invoice.upload,
      updatedAt: invoice.updated_at,
      paymentData: invoice.payment,
      branchDetails: invoice.branch_details,
      createdAt: invoice.created_at,
      approvalDetails: invoice.approval_details,
      firstName: invoice.first_name,
      lastName: invoice.last_name,
      companyName: invoice.company_name,
      commentCount: invoice.comment_count,
      id: invoice.others_id,
      isLock: invoice.is_lock,
      totalAllocatedAmount: invoice.total_allocated_amount,
      isAllocated: invoice.is_allocated,
      isInvoiceBankStatementCreated: invoice.is_invoice_bank_statement_created,
      source: invoice?.source,
    });

    return acc;
  }, []);
}

export const createInitialStateForOthersForm = ({
  mode,
  initialState,
}: {
  mode: InvoicingAdvanceMode;
  initialState: InitialStateInvoicingAdvanceType["singleOtherInvoiceDetails"];
}) => {
  const fetchers = createFetchers();
  if (mode === "add") {
    return {
      primaryDetails: {},
      paymentDetails: [{}],
    };
  }
  return {
    primaryDetails: createPrimaryDataForOthers(initialState, fetchers),
    paymentDetails: createPaymentDataForOthers(initialState, fetchers),
  };
};

export const createPrimaryDataForOthers = (
  initialState: InitialStateInvoicingAdvanceType["singleOtherInvoiceDetails"],
  fetchers: ExtendedFetchers
) => {
  return {
    branch: fetchers["branch"]?.toOption(initialState?.branch_details as any),
    date_of_invoice: initialState?.date_of_invoice ?? "",
    narration_in_bank: initialState?.narration_in_bank ?? "",
    additional_comments: initialState?.additional_comments ?? "",
    upload: initialState?.upload ?? [],
  };
};

export const createPaymentDataForOthers = (
  initialState: InitialStateInvoicingAdvanceType["singleOtherInvoiceDetails"],
  fetchers: ExtendedFetchers
) => {
  if (initialState) {
    return initialState.payment.map((singlePayment) => {
      const isDebit = singlePayment.transaction_type === "debit";
      const value = isDebit ? TRANSACTION_TYPES[0] : TRANSACTION_TYPES[1];
      return {
        ["sent/received"]: value,
        mode_of_payment: fetchers["mode_of_payment"].toOption(
          singlePayment.account_details as any
        ),
        ["sent/received amount"]: singlePayment.amount,
        allocation_data: singlePayment.allocation_details,
      };
    });
  }
  return [{}];
};

export function createInitialStateCustomerSupplierRef(
  initialState:
    | InitialStateInvoicingAdvanceType["singleInvoiceDetails"]
    | undefined,
  tabType: InvoiceType
) {
  const key = tabType === "expenses" ? "supplier_name" : "customer_name";
  const columnValue = initialState?.invoice_data?.primary.find(
    (columns) => columns.name === key
  );

  if (columnValue) {
    const fetchers = createFetchers();
    const fetcher = fetchers[key as keyof ExtendedFetchers];
    if (fetcher.columnName === key) {
      return fetcher.toOption(columnValue as any);
    }
  }
  return null;
}
export const getSubValue = (value: valueType): string | null => {
  if (typeof value === "object" && value !== null && "subValue" in value) {
    return value?.subValue ?? null;
  }
  return null;
};

export const checkVisibleColumn = (column: SingleColumnType): boolean => {
  switch (column.column_name) {
    case "alternative_unit":
    case "alternative_rate":
    case "alternative_quantity":
      return false;

    default:
      return true;
  }
};

export const validateMultipleEmails = (emails: string[]) => {
  return emails.every((email) => email !== "" && isEmailValid(email));
};

export function initializePrevSupplierLedgerIdRef(
  mode: InvoicingAdvanceMode,
  initialState:
    | InitialStateInvoicingAdvanceType["singleInvoiceDetails"]
    | undefined,
  tabType: InvoiceType
): string | null {
  if (mode === "add" || !initialState) {
    return null;
  }

  const key = tabType === "expenses" ? "supplier_name" : "customer_name";

  const columnValue = initialState.invoice_data.primary.find(
    (col) => col.column_name === key
  )?.column_value;

  if (
    columnValue &&
    typeof columnValue === "object" &&
    "ledger_id" in columnValue
  ) {
    return String(
      (columnValue as { ledger_id: string | number }).ledger_id ?? ""
    );
  }

  return null;
}

/**
 * Returns the applicable rate from a date-versioned rates array.
 * Picks the rate whose date is the most recent one that is on or before
 * the invoice date. Falls back to the first element when no match found
 * or when no invoice date is provided.
 */
export function pickRateByDate(
  rates: { date: string; rate: number }[],
  invoiceDate?: string | null
): number {
  if (!rates.length) return 0;
  if (!invoiceDate) return rates[0]?.rate ?? 0;

  const inv = moment(invoiceDate, [
    "YYYY-MM-DD",
    "DD-MM-YYYY",
    moment.ISO_8601,
  ]);
  if (!inv.isValid()) return rates[0]?.rate ?? 0;

  const applicable = rates
    .filter((r) => r.date && moment(r.date).isSameOrBefore(inv, "day"))
    .sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf());

  return applicable[0]?.rate ?? rates[0]?.rate ?? 0;
}

/**
 * Returns the appropriate default TDS/TCS detail from a customer/supplier record
 * based on the current tab (expenses/income) and TDS vs TCS selection.
 */
export function getDefaultTDSTCSFromCS(
  cs: CustomerSupplierListType | null | undefined,
  tabType: InvoiceType | null | undefined,
  tdsOrTcs: "tds" | "tcs"
): DefaultTdsTcsDetailsType | null {
  if (!cs) return null;
  if (tdsOrTcs === "tds") {
    return tabType === "expenses"
      ? (cs.default_tds_expense_details ?? null)
      : (cs.default_tds_income_details ?? null);
  }
  return tabType === "expenses"
    ? (cs.default_tcs_expense_details ?? null)
    : (cs.default_tcs_income_details ?? null);
}
